import type { ILanguageResources, ITranslationModule } from '../../../../interfaces';
import { interpolate } from '../../../../global';

// Column Titles, Table Titles, Dialogs, UI Strings
class Id {
  public static readonly prefix = 'UI.scripts.dealer-franchisee.quotes.views.FranchiseeQuoteListView.';
  // Table column headers
  public static readonly quoteNumberTitle = `${Id.prefix}quoteNumberTitle`;
  public static readonly quoteTitle = `${Id.prefix}quoteTitle`;
  public static readonly projectTitle = `${Id.prefix}projectTitle`;
  public static readonly clientTitle = `${Id.prefix}clientTitle`;
  public static readonly authorTitle = `${Id.prefix}authorTitle`;
  public static readonly lastModifiedTitle = `${Id.prefix}lastModifiedTitle`;
  public static readonly statusTitle = `${Id.prefix}statusTitle`;
  public static readonly fabricationItemsInState = `${Id.prefix}fabricationItemsInState`;
  public static readonly fabricationTooltipHeading = `${Id.prefix}fabricationTooltipHeading`;
  public static readonly netAmountTitle = `${Id.prefix}netAmountTitle`;
  // Advanced filter radio
  public static readonly allQuotesRadio = `${Id.prefix}allQuotesRadio`;
  public static readonly myQuotesRadio = `${Id.prefix}myQuotesRadio`;
  // Dialogs
  public static readonly deleteQuoteConfirmation = `${Id.prefix}deleteQuoteConfirmation`;
  // Table section titles
  public static readonly allCurrentTableTitle = `${Id.prefix}allCurrentTableTitle`;
  public static readonly draftTableTitle = `${Id.prefix}draftTableTitle`;
  public static readonly activeTableTitle = `${Id.prefix}activeTableTitle`;
  public static readonly issuedTableTitle = `${Id.prefix}issuedTableTitle`;
  public static readonly acceptedTableTitle = `${Id.prefix}acceptedTableTitle`;
  public static readonly underReviewTableTitle = `${Id.prefix}underReviewTableTitle`;
  public static readonly reviewedTableTitle = `${Id.prefix}reviewedTableTitle`;
  public static readonly fabricatedTableTitle = `${Id.prefix}fabricatedTableTitle`;
  public static readonly archivedTableTitle = `${Id.prefix}archivedTableTitle`;
  // Add for Discard button
  public static readonly discardButton = `${Id.prefix}discardButton`;

  // Toasts (commented out, add if you use them)
  // public static readonly quotesRefreshedToast = `${Id.prefix}quotesRefreshedToast`;
  // public static readonly updatesComingSoonToast = `${Id.prefix}updatesComingSoonToast`;
  // public static readonly moreUpdatesComingToast = `${Id.prefix}moreUpdatesComingToast`;
  public static readonly quoteCopiedSuccessfully = `${Id.prefix}quoteCopiedSuccessfully`;
  public static readonly quoteCopyFailed = `${Id.prefix}quoteCopyFailed`;
  public static readonly copyButton = `${Id.prefix}copyButton`;
  public static readonly deleteButton = `${Id.prefix}deleteButton`;
  public static readonly termsButton = `${Id.prefix}termsButton`;
  public static readonly termsPickButton = `${Id.prefix}termsPickButton`;
  public static readonly termsClearButton = `${Id.prefix}termsClearButton`;
  public static readonly viewQuote = `${Id.prefix}viewQuote`;
  // Tab titles
  public static readonly quoteDefaultsTab = `${Id.prefix}quoteDefaultsTab`;
  // Reset to Active feature
  public static readonly resetToActiveButton = `${Id.prefix}resetToActiveButton`;
  public static readonly resetToActiveConfirmation = `${Id.prefix}resetToActiveConfirmation`;
  public static readonly resetToActiveSubmitted = `${Id.prefix}resetToActiveSubmitted`;
  public static readonly resetToActiveFailed = `${Id.prefix}resetToActiveFailed`;
  public static readonly resetToActiveError = `${Id.prefix}resetToActiveError`;
  // Cancel / Accept / Reject from the ellipsis (Task 229184)
  public static readonly quoteStateChanged = `${Id.prefix}quoteStateChanged`;
  public static readonly quoteDeleted = `${Id.prefix}quoteDeleted`;
  public static readonly failedProcessMessage = `${Id.prefix}failedProcessMessage`;
  public static readonly quoteLockedMessage = `${Id.prefix}quoteLockedMessage`;
  public static readonly quoteLockedTitle = `${Id.prefix}quoteLockedTitle`;
  public static readonly supplierOfflineMessage = `${Id.prefix}supplierOfflineMessage`;
  public static readonly supplierOfflineTitle = `${Id.prefix}supplierOfflineTitle`;
  public static readonly cancelButton = `${Id.prefix}cancelButton`;
  public static readonly acceptButton = `${Id.prefix}acceptButton`;
  public static readonly approveButton = `${Id.prefix}approveButton`;
  public static readonly rejectButton = `${Id.prefix}rejectButton`;

  // Row-action dialog (Task 229184). Each action has a title and an explanatory legend; blocked
  // actions swap the legend for the reason they are blocked.
  public static readonly actionDialogTitle = `${Id.prefix}actionDialogTitle`;
  public static readonly actionDialogUnavailable = `${Id.prefix}actionDialogUnavailable`;
  public static readonly actionDialogClose = `${Id.prefix}actionDialogClose`;
  public static readonly copyAlternativeTitle = `${Id.prefix}copyAlternativeTitle`;
  public static readonly copyAlternativeLegend = `${Id.prefix}copyAlternativeLegend`;
  public static readonly copyNewProjectQuoteTitle = `${Id.prefix}copyNewProjectQuoteTitle`;
  public static readonly copyNewProjectQuoteLegend = `${Id.prefix}copyNewProjectQuoteLegend`;
  public static readonly copyOutOfProjectTitle = `${Id.prefix}copyOutOfProjectTitle`;
  public static readonly copyOutOfProjectLegend = `${Id.prefix}copyOutOfProjectLegend`;
  public static readonly cancelLegend = `${Id.prefix}cancelLegend`;
  public static readonly acceptLegend = `${Id.prefix}acceptLegend`;
  public static readonly approveLegend = `${Id.prefix}approveLegend`;
  public static readonly rejectLegend = `${Id.prefix}rejectLegend`;
  public static readonly deleteLegend = `${Id.prefix}deleteLegend`;
  public static readonly resetToActiveLegend = `${Id.prefix}resetToActiveLegend`;
  public static readonly reasonCopying = `${Id.prefix}reasonCopying`;
  public static readonly reasonDeleting = `${Id.prefix}reasonDeleting`;
  public static readonly reasonLockedByOther = `${Id.prefix}reasonLockedByOther`;
  public static readonly reasonLockedBySelf = `${Id.prefix}reasonLockedBySelf`;
  public static readonly reasonProjectInactive = `${Id.prefix}reasonProjectInactive`;
  public static readonly reasonSetWillNotTakeAnother = `${Id.prefix}reasonSetWillNotTakeAnother`;
  public static readonly reasonNotAllowedInThisState = `${Id.prefix}reasonNotAllowedInThisState`;
  public static readonly lockBadgeCopying = `${Id.prefix}lockBadgeCopying`;
  public static readonly lockBadgeDeleting = `${Id.prefix}lockBadgeDeleting`;

  private constructor() {}
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class LangFranchiseeQuoteListView {
  private constructor() {}

  // Table column headers
  public static quoteNumberTitle(): string {
    return interpolate(Id.quoteNumberTitle);
  }

  public static quoteTitle(): string {
    return interpolate(Id.quoteTitle);
  }

  public static projectTitle(): string {
    return interpolate(Id.projectTitle);
  }

  public static clientTitle(): string {
    return interpolate(Id.clientTitle);
  }

  public static authorTitle(): string {
    return interpolate(Id.authorTitle);
  }

  public static lastModifiedTitle(): string {
    return interpolate(Id.lastModifiedTitle);
  }

  public static statusTitle(): string {
    return interpolate(Id.statusTitle);
  }

  public static fabricationItemsInState(count: number, state: string): string {
    return interpolate(Id.fabricationItemsInState, { count: count.toString(), state });
  }

  public static fabricationTooltipHeading(): string {
    return interpolate(Id.fabricationTooltipHeading);
  }

  public static netAmountTitle(): string {
    return interpolate(Id.netAmountTitle);
  }

  // Advanced filter radio
  public static allQuotesRadio(): string {
    return interpolate(Id.allQuotesRadio);
  }

  public static myQuotesRadio(): string {
    return interpolate(Id.myQuotesRadio);
  }

  // Dialogs
  public static deleteQuoteConfirmation(params: { quoteNumber: string; quoteTitle: string }): string {
    return interpolate(Id.deleteQuoteConfirmation, params);
  }

  // Table section titles
  public static allCurrentTableTitle(): string {
    return interpolate(Id.allCurrentTableTitle);
  }

  public static draftTableTitle(): string {
    return interpolate(Id.draftTableTitle);
  }

  public static activeTableTitle(): string {
    return interpolate(Id.activeTableTitle);
  }

  public static issuedTableTitle(): string {
    return interpolate(Id.issuedTableTitle);
  }

  public static acceptedTableTitle(): string {
    return interpolate(Id.acceptedTableTitle);
  }

  public static underReviewTableTitle(): string {
    return interpolate(Id.underReviewTableTitle);
  }

  public static reviewedTableTitle(): string {
    return interpolate(Id.reviewedTableTitle);
  }

  public static fabricatedTableTitle(): string {
    return interpolate(Id.fabricatedTableTitle);
  }

  public static archivedTableTitle(): string {
    return interpolate(Id.archivedTableTitle);
  }

  // Toasts (add if you use them)
  // public static quotesRefreshedToast(): string {
  //   return interpolate(Id.quotesRefreshedToast);
  // }
  // public static updatesComingSoonToast(): string {
  //   return interpolate(Id.updatesComingSoonToast);
  // }
  // public static moreUpdatesComingToast(): string {
  //   return interpolate(Id.moreUpdatesComingToast);
  // }

  public static quoteCopiedSuccessfully(quoteNumber: string, quoteTitle: string): string {
    return interpolate(Id.quoteCopiedSuccessfully, { quoteNumber, quoteTitle });
  }

  public static quoteCopyFailed(quoteNumber: string, quoteTitle: string, errorMessage: string): string {
    return interpolate(Id.quoteCopyFailed, { quoteNumber, quoteTitle, errorMessage });
  }

  // Add for Discard button
  public static discardButton(): string {
    return interpolate(Id.discardButton);
  }

  public static copyButton(): string {
    return interpolate(Id.copyButton);
  }

  public static deleteButton(): string {
    return interpolate(Id.deleteButton);
  }

  public static resetToActiveButton(): string {
    return interpolate(Id.resetToActiveButton);
  }

  public static resetToActiveConfirmation(): string {
    return interpolate(Id.resetToActiveConfirmation);
  }

  public static resetToActiveSubmitted(): string {
    return interpolate(Id.resetToActiveSubmitted);
  }

  public static resetToActiveFailed(): string {
    return interpolate(Id.resetToActiveFailed);
  }

  public static resetToActiveError(): string {
    return interpolate(Id.resetToActiveError);
  }

  public static termsButton(): string {
    return interpolate(Id.termsButton);
  }

  public static termsPickButton(): string {
    return interpolate(Id.termsPickButton);
  }

  public static termsClearButton(): string {
    return interpolate(Id.termsClearButton);
  }

  public static viewQuote(): string {
    return interpolate(Id.viewQuote);
  }

  // Tab titles
  public static quoteDefaultsTab(): string {
    return interpolate(Id.quoteDefaultsTab);
  }

  // Cancel / Accept / Reject from the ellipsis (Task 229184)
  public static quoteDeleted(): string {
    return interpolate(Id.quoteDeleted);
  }
  public static quoteStateChanged(): string {
    return interpolate(Id.quoteStateChanged);
  }

  public static failedProcessMessage(): string {
    return interpolate(Id.failedProcessMessage);
  }

  public static quoteLockedMessage(params: { ownerName: string }): string {
    return interpolate(Id.quoteLockedMessage, params);
  }

  public static quoteLockedTitle(): string {
    return interpolate(Id.quoteLockedTitle);
  }

  public static supplierOfflineMessage(): string {
    return interpolate(Id.supplierOfflineMessage);
  }

  public static supplierOfflineTitle(): string {
    return interpolate(Id.supplierOfflineTitle);
  }

  public static cancelButton(): string {
    return interpolate(Id.cancelButton);
  }

  public static acceptButton(): string {
    return interpolate(Id.acceptButton);
  }

  public static approveButton(): string {
    return interpolate(Id.approveButton);
  }

  public static rejectButton(): string {
    return interpolate(Id.rejectButton);
  }

  // ----- row-action dialog (Task 229184) -----
  public static actionDialogTitle(params: { quoteNumber: string; quoteTitle: string }): string {
    return interpolate(Id.actionDialogTitle, params);
  }
  public static actionDialogUnavailable(): string {
    return interpolate(Id.actionDialogUnavailable);
  }
  public static actionDialogClose(): string {
    return interpolate(Id.actionDialogClose);
  }
  public static copyAlternativeTitle(): string {
    return interpolate(Id.copyAlternativeTitle);
  }
  public static copyAlternativeLegend(): string {
    return interpolate(Id.copyAlternativeLegend);
  }
  public static copyNewProjectQuoteTitle(): string {
    return interpolate(Id.copyNewProjectQuoteTitle);
  }
  public static copyNewProjectQuoteLegend(): string {
    return interpolate(Id.copyNewProjectQuoteLegend);
  }
  public static copyOutOfProjectTitle(): string {
    return interpolate(Id.copyOutOfProjectTitle);
  }
  public static copyOutOfProjectLegend(): string {
    return interpolate(Id.copyOutOfProjectLegend);
  }
  public static cancelLegend(): string {
    return interpolate(Id.cancelLegend);
  }
  public static acceptLegend(): string {
    return interpolate(Id.acceptLegend);
  }
  public static approveLegend(): string {
    return interpolate(Id.approveLegend);
  }
  public static rejectLegend(): string {
    return interpolate(Id.rejectLegend);
  }
  public static deleteLegend(): string {
    return interpolate(Id.deleteLegend);
  }
  public static resetToActiveLegend(): string {
    return interpolate(Id.resetToActiveLegend);
  }
  public static reasonCopying(): string {
    return interpolate(Id.reasonCopying);
  }
  public static reasonDeleting(): string {
    return interpolate(Id.reasonDeleting);
  }
  public static reasonLockedByOther(params: { ownerName: string }): string {
    return interpolate(Id.reasonLockedByOther, params);
  }
  public static reasonLockedBySelf(): string {
    return interpolate(Id.reasonLockedBySelf);
  }
  public static reasonProjectInactive(): string {
    return interpolate(Id.reasonProjectInactive);
  }
  public static reasonSetWillNotTakeAnother(): string {
    return interpolate(Id.reasonSetWillNotTakeAnother);
  }
  public static reasonNotAllowedInThisState(): string {
    return interpolate(Id.reasonNotAllowedInThisState);
  }
  public static lockBadgeCopying(): string {
    return interpolate(Id.lockBadgeCopying);
  }
  public static lockBadgeDeleting(): string {
    return interpolate(Id.lockBadgeDeleting);
  }
}

export class FranchiseeQuoteListViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    // Table column headers
    register.add(Id.quoteNumberTitle, "{{glossary 'quote'}} No.", 'Column header for Quote Number');
    register.add(Id.quoteTitle, "{{glossary 'quote'}} Title", 'Column header for Quote Title');
    register.add(Id.projectTitle, "{{glossary 'project'}} Title", 'Column header for Project Title');
    register.add(Id.clientTitle, "{{glossary 'client'}}", 'Column header for Client');
    register.add(Id.authorTitle, "{{glossary 'author'}}", 'Column header for Author');
    register.add(Id.lastModifiedTitle, 'Last Modified', 'Column header for Last Modified');
    register.add(Id.statusTitle, 'Status', 'Column header for Status');
    register.add(
      Id.fabricationItemsInState,
      '{{count}} in {{state}}',
      'One line of the quote list fabrication tooltip, when the quote spans several states'
    );
    register.add(
      Id.fabricationTooltipHeading,
      'Fabrication status:',
      'Heading line of the quote list fabrication tooltip'
    );
    register.add(Id.netAmountTitle, 'Net Amount', 'Column header for Net Amount');

    // Advanced filter radio
    register.add(Id.allQuotesRadio, "All {{glossary 'quote:plural'}}", 'Radio button for all quotes');
    register.add(Id.myQuotesRadio, "My {{glossary 'quote:plural'}}", 'Radio button for my quotes');

    // Dialogs
    register.add(
      Id.deleteQuoteConfirmation,
      "Are you sure you'd like to delete {{quoteNumber}} — {{quoteTitle}}?",
      // Names the quote. The generic "delete the Quote?" gave the user nothing to check the dialog
      // against, and a mis-clicked row in a long list is unrecoverable. quoteNumber reads "Draft" for
      // an unnumbered quote, which is exactly what its row shows.
      'Confirmation dialog for deleting a quote, naming the quote being deleted'
    );

    // Cancel / Accept / Reject from the ellipsis (Task 229184)
    register.add(Id.quoteStateChanged, "{{glossary 'quote'}} updated", 'Toast when a quote state change succeeds');
    register.add(Id.quoteDeleted, "{{glossary 'quote'}} deleted", 'Toast when a quote is successfully deleted');
    register.add(
      Id.failedProcessMessage,
      "State change is currently unavailable due to an internal error. Would you like to open the {{glossary 'quote'}} to apply the change?",
      'Message when a list-view state change fails; offers to open the quote'
    );
    register.add(
      Id.quoteLockedMessage,
      "This {{glossary 'quote'}} is currently being edited by {{ownerName}}. You cannot perform actions on it at this time.",
      'Message when a quote is locked by another user'
    );
    register.add(Id.quoteLockedTitle, "{{glossary 'quote'}} Locked", 'Title for the quote-locked dialog');
    register.add(
      Id.supplierOfflineMessage,
      'The supplier is currently offline. Please try again later.',
      'Message when the supplier is offline for a state change'
    );
    register.add(Id.supplierOfflineTitle, 'Supplier Offline', 'Title for the supplier-offline dialog');
    register.add(Id.cancelButton, 'Cancel', 'Ellipsis action: cancel the quote');
    register.add(Id.acceptButton, 'Accept', 'Ellipsis action: accept the quote');
    register.add(Id.approveButton, 'Approve', 'Ellipsis action: approve the quote');
    register.add(Id.rejectButton, 'Reject', 'Ellipsis action: reject the quote');

    // Table section titles
    register.add(Id.allCurrentTableTitle, 'All Current', 'Table section title for all current quotes');
    register.add(Id.draftTableTitle, 'Draft', 'Table section title for draft quotes');
    register.add(Id.activeTableTitle, 'Active', 'Table section title for active quotes');
    register.add(Id.issuedTableTitle, 'Issued', 'Table section title for issued quotes');
    register.add(Id.acceptedTableTitle, 'Accepted', 'Table section title for accepted quotes');
    register.add(Id.underReviewTableTitle, 'Under Review', 'Table section title for under review quotes');
    register.add(Id.reviewedTableTitle, 'Reviewed', 'Table section title for reviewed quotes');
    register.add(Id.fabricatedTableTitle, 'Fabrication', 'Table section title for fabricated quotes');
    register.add(Id.archivedTableTitle, 'Archived', 'Table section title for archived quotes');

    // Toasts (add if you use them)
    // register.add(Id.quotesRefreshedToast, "Your Quotes have been refreshed", 'Toast: Quotes refreshed');
    // register.add(Id.updatesComingSoonToast, "Updates are coming soon", 'Toast: Updates coming soon');
    // register.add(Id.moreUpdatesComingToast, "More Updates are coming, refreshing soon", 'Toast: More updates coming');

    register.add(
      Id.quoteCopiedSuccessfully,
      "{{glossary 'quote'}} '{{quoteNumber}}' '{{quoteTitle}}' copy completed",
      'Toast shown when a quote finishes copying'
    );

    register.add(
      Id.quoteCopyFailed,
      "{{glossary 'quote'}} '{{quoteNumber}}' '{{quoteTitle}}' copy failed. {{errorMessage}}",
      'Toast shown when a quote copy fails'
    );

    // Add for Discard button
    register.add(Id.discardButton, 'Discard', 'Button label for discarding/cancelling an action');
    register.add(Id.copyButton, 'Copy', 'Button label for copying a quote');
    register.add(Id.deleteButton, 'Delete', 'Button label for deleting a quote');
    register.add(Id.termsButton, 'Choose', 'Button label for terms and conditions');
    register.add(
      Id.termsPickButton,
      "Select {{glossary 'terms-conditions'}}",
      'Button label for picking terms and conditions'
    );
    register.add(
      Id.termsClearButton,
      "Clear {{glossary 'terms-conditions'}}",
      'Button label for clearing terms and conditions'
    );
    register.add(Id.viewQuote, 'View Quote', 'Button label for opening a quote');

    // Reset to Active feature
    register.add(
      Id.resetToActiveButton,
      'Reset to Active',
      'Button label for resetting quote from IssuePending to Active'
    );
    register.add(
      Id.resetToActiveConfirmation,
      'Are you sure you want to reset this quote to Active status? Any pending documents will be marked as failed.',
      'Confirmation message when resetting a quote from IssuePending to Active'
    );
    register.add(
      Id.resetToActiveSubmitted,
      'Quote reset request submitted. The quote will be updated shortly.',
      'Toast message shown after submitting a reset quote request'
    );
    register.add(
      Id.resetToActiveFailed,
      'Failed to reset quote. Please try again.',
      'Error message when reset quote operation fails'
    );
    register.add(
      Id.resetToActiveError,
      'An error occurred while resetting the quote. Please try again.',
      'Error message when reset quote API call throws an exception'
    );

    // Tab titles
    register.add(Id.quoteDefaultsTab, 'Defaults', 'Tab title for quote defaults');

    // Row-action dialog (Task 229184). The header names the quote so the user can confirm which row
    // they clicked; each card carries a legend saying what the action does, and a blocked card
    // replaces that legend with the reason it is blocked.
    register.add(
      Id.actionDialogTitle,
      '{{quoteNumber}} — {{quoteTitle}}',
      // No glossary prefix: quoteNumber is already formatted as "QTE: 001028", so prefixing it read
      // "Quote QTE: 001028". The dialog opens from a quote row; it does not need to say "Quote".
      'Header of the quote row-action dialog, naming the quote being acted on'
    );
    register.add(Id.actionDialogUnavailable, 'Unavailable', 'Badge on a row-action card that cannot currently be used');
    register.add(Id.actionDialogClose, 'Cancel', 'Dismiss button on the quote row-action dialog');
    register.add(Id.copyAlternativeTitle, 'Copy as alternative', 'Row-action card title');
    register.add(
      Id.copyAlternativeLegend,
      "Adds another option to this {{glossary 'quote'}} group, so the {{glossary 'client'}} can compare them side by side.",
      'Row-action card legend for copy as alternative'
    );
    register.add(
      Id.copyNewProjectQuoteTitle,
      "New {{glossary 'quote'}} in this {{glossary 'project'}}",
      'Row-action card title'
    );
    register.add(
      Id.copyNewProjectQuoteLegend,
      "Creates a separate {{glossary 'quote'}} that stays in this {{glossary 'project'}} and keeps its delivery address.",
      'Row-action card legend for copy as a new project quote'
    );
    register.add(Id.copyOutOfProjectTitle, "Copy to a standalone {{glossary 'quote'}}", 'Row-action card title');
    register.add(
      Id.copyOutOfProjectLegend,
      "Creates an independent copy outside this {{glossary 'project'}}, starting with a blank delivery address.",
      'Row-action card legend for copy out of project'
    );
    register.add(
      Id.cancelLegend,
      "Stops work on this {{glossary 'quote'}}. It stays on record and can be reviewed later.",
      'Row-action card legend for cancel'
    );
    register.add(
      Id.acceptLegend,
      "Marks the {{glossary 'quote'}} as accepted by the {{glossary 'client'}} so it can proceed to order.",
      'Row-action card legend for accept'
    );
    register.add(
      Id.approveLegend,
      "Approves the {{glossary 'quote'}} so the order can be placed with the {{glossary 'supplier'}}.",
      'Row-action card legend for approve'
    );
    register.add(
      Id.rejectLegend,
      "Records that the {{glossary 'client'}} declined this {{glossary 'quote'}}.",
      'Row-action card legend for reject'
    );
    register.add(
      Id.deleteLegend,
      'Permanently removes this draft and everything on it. This cannot be undone.',
      'Row-action card legend for delete'
    );
    register.add(
      Id.resetToActiveLegend,
      'Returns the quote to Active so it can be edited and issued again.',
      'Row-action card legend for reset to active'
    );
    register.add(
      Id.reasonCopying,
      'A copy of this quote is in progress. This will be available again once it finishes.',
      'Reason a row action is blocked while a copy is running'
    );
    register.add(
      Id.reasonDeleting,
      'This quote is being deleted.',
      'Reason a row action is blocked while a delete is running'
    );
    register.add(
      Id.reasonLockedByOther,
      'In use by {{ownerName}}. This will be available again once they close it.',
      'Reason a row action is blocked because another user holds the quote'
    );
    register.add(
      Id.reasonLockedBySelf,
      'You have this quote open in another window. Close it to continue here.',
      'Reason a row action is blocked because the same user holds the quote elsewhere'
    );
    register.add(
      Id.reasonProjectInactive,
      "This {{glossary 'project'}} is closed, so new {{glossary 'quote'}}s cannot be added to it.",
      'Reason a copy action is blocked because the project is cancelled or completed'
    );
    register.add(
      Id.reasonSetWillNotTakeAnother,
      "This {{glossary 'quote'}} group cannot take another alternative.",
      'Reason the alternative copy action is blocked'
    );
    register.add(
      Id.reasonNotAllowedInThisState,
      "Not available for this {{glossary 'quote'}} right now.",
      'Fallback reason a row action is unavailable'
    );
    // Passive row badges for the quote's LOCK state. Deliberately terse - they sit beside the state
    // badge in a narrow column, and the row-action dialog carries the full explanation.
    register.add(Id.lockBadgeCopying, 'Copying', 'Row badge shown on the SOURCE quote while a copy of it runs');
    register.add(Id.lockBadgeDeleting, 'Deleting', 'Row badge shown while a delete of the quote runs');
  }
}
